import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Avatar
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X } from 'lucide-react';
import { getAllFlags, createFlag, updateFlag, deleteFlag } from '../../Services/ContentHub/FlagsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const FlagsData = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentFlag, setCurrentFlag] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        countryCode: '',
        currencyCode: '',
        isDefault: false,
        isActive: true
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [modalError, setModalError] = useState(null);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedFlagId, setSelectedFlagId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Fetch flags
    const fetchFlagsData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllFlags();
            if (response && response.success) {
                setFlags(response.data);
            } else {
                setError("Failed to load country codes.");
            }
        } catch (err) {
            setError("Failed to fetch country codes. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFlagsData();
    }, [fetchFlagsData]);

    const handleOpenModal = (flag = null) => {
        if (flag) {
            setIsEdit(true);
            setCurrentFlag(flag);
            setFormData({
                name: flag.name || '',
                countryCode: flag.countryCode || '',
                currencyCode: flag.currencyCode || '',
                isDefault: flag.isDefault || false,
                isActive: flag.isActive || false
            });
            setImagePreview(flag.symbol || null);
        } else {
            setIsEdit(false);
            setCurrentFlag(null);
            setFormData({
                name: '',
                countryCode: '',
                currencyCode: '',
                isDefault: false,
                isActive: true
            });
            setImagePreview(null);
        }
        setSelectedImage(null);
        setValidationErrors({});
        setModalError(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentFlag(null);
        setFormData({
            name: '',
            countryCode: '',
            currencyCode: '',
            isDefault: false,
            isActive: true
        });
        setSelectedImage(null);
        setImagePreview(null);
        setModalError(null);
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;

        if (name === 'name') {
            newValue = value.replace(/[^a-zA-Z\s]/g, '');
        } else if (name === 'countryCode') {
            if (value === '+') {
                newValue = '+';
            } else if (value.startsWith('+')) {
                newValue = '+' + value.slice(1).replace(/\D/g, '');
            } else {
                const digits = value.replace(/\D/g, '');
                newValue = digits ? '+' + digits : '';
            }
        } else if (name === 'currencyCode') {
            newValue = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5);
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
                setValidationErrors(prev => ({ ...prev, symbolImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setSelectedImage(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            // Clear image-specific error
            if (validationErrors.symbolImage) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.symbolImage;
                    return newErrors;
                });
            }
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleSubmit = async () => {
        const clientErrors = {};
        if (!formData.name) clientErrors.name = "Country name is required";
        if (!formData.countryCode) clientErrors.countryCode = "Country code is required";

        if (selectedImage && !selectedImage.type.startsWith("image/")) {
            clientErrors.symbolImage = "Only image files are allowed";
            setModalError("Only image files are allowed");
            showSnackbar("Only image files are allowed", "error");
        }

        if (Object.keys(clientErrors).length > 0) {
            setValidationErrors(clientErrors);
            setModalError("Please fix the validation errors");
            return;
        }

        if (!selectedImage && !isEdit) {
            setValidationErrors({ symbolImage: "Please select a symbol image." });
            setModalError("Please select a symbol image.");
            return;
        }

        setSubmitting(true);
        setModalError(null);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('countryCode', formData.countryCode);
        data.append('currencyCode', formData.currencyCode);
        data.append('isDefault', formData.isDefault);
        data.append('isActive', formData.isActive);

        if (selectedImage) {
            data.append('symbolImage', selectedImage);
        }

        try {
            if (isEdit) {
                await updateFlag(currentFlag.id, data);
                showSnackbar("Country code updated successfully!", "success");
            } else {
                await createFlag(data);
                showSnackbar("Country code created successfully!", "success");
            }

            handleCloseModal();
            fetchFlagsData();
        } catch (err) {
            const serverError = err.response?.data;
            const message = serverError?.message || serverError?.error?.message || serverError?.errors?.[0] || `Failed to ${isEdit ? 'update' : 'create'} country code`;

            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    fieldErrors[detail.field] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else if (message.toLowerCase().includes('image')) {
                // If it's an image error but not in 'details', show it for the image field specifically
                setValidationErrors(prev => ({ ...prev, symbolImage: message }));
            }

            setModalError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedFlagId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedFlagId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteFlag(selectedFlagId);
                if (response && response.success) {
                    showSnackbar("Country code deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedFlagId(null);
                    fetchFlagsData();
                } else {
                    setDeleteError(response?.message || "Failed to delete country code");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete country code";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Country Codes & Flags
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button onClick={() => handleOpenModal()}
                            disableElevation disableRipple disableFocusRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={18} />}
                            sx={{
                                bgcolor: '#FFBD65',

                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                height: '40px',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Country Code
                        </Button>
                    </PermissionWrapper>
                </Box>

                {!openModal && error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1200 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '80px' }}>Symbol</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Country Code</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Currency Code</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Default</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : flags.length > 0 ? (
                                    flags.map((flag, idx) => (
                                        <TableRow key={flag.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar src={flag.symbol} variant="rounded" sx={{ width: 32, height: 32, borderRadius: '4px', fontSize: '0.85rem' }}>
                                                    {flag.name?.charAt(0)}
                                                </Avatar>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{flag.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{flag.countryCode}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{flag.currencyCode}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: flag.isDefault ? '#E8F5E9' : '#F5F5F5',
                                                    color: flag.isDefault ? '#2E7D32' : '#757575',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {flag.isDefault ? 'Yes' : 'No'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: flag.isActive ? '#E8F5E9' : '#FFEBEE',
                                                    color: flag.isActive ? '#2E7D32' : '#D32F2F',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {flag.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {flag.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {flag.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(flag.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(flag.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FFBD65' }}
                                                                onClick={() => handleOpenModal(flag)}
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small"
                                                                sx={{ color: '#FF4D49' }}
                                                                onClick={() => handleDeleteClick(flag.id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No country codes found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Country Code' : 'Add New Country Code'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && <Alert severity="error" sx={{ mb: 2, borderRadius: '6px', fontSize: '0.8rem' }} onClose={() => setModalError(null)}>{modalError}</Alert>}
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Country Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. United States"
                                required
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Country Code"
                                name="countryCode"
                                value={formData.countryCode}
                                onChange={handleInputChange}
                                placeholder="e.g. +91, +1"
                                required
                                error={!!validationErrors.countryCode}
                                helperText={validationErrors.countryCode || "Note: Enter dial code starting with '+' (e.g. +91, +1)"}
                                FormHelperTextProps={{
                                    sx: { color: validationErrors.countryCode ? undefined : '#666', fontSize: '0.75rem' }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Currency Code"
                                name="currencyCode"
                                value={formData.currencyCode}
                                onChange={handleInputChange}
                                placeholder="e.g. USD, INR"
                                error={!!validationErrors.currencyCode}
                                helperText={validationErrors.currencyCode || "Note: Enter currency code in uppercase (e.g. INR, USD)"}
                                FormHelperTextProps={{
                                    sx: { color: validationErrors.currencyCode ? undefined : '#666', fontSize: '0.75rem' }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                <FormControlLabel
                                    control={<Switch checked={formData.isDefault} onChange={handleInputChange} name="isDefault"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />}
                                    label={<Typography sx={{ fontSize: '0.85rem' }}>Set as Default</Typography>}
                                />
                                <FormControlLabel
                                    control={<Switch checked={formData.isActive} onChange={handleInputChange} name="isActive"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />}
                                    label={<Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>Symbol / Flag Image</Typography>
                                <Box
                                    sx={{
                                        border: validationErrors.symbolImage ? '2px dashed #FF4D49' : '2px dashed #E0E0E0',
                                        borderRadius: '6px', p: 2,
                                        textAlign: 'center',
                                        bgcolor: '#FAFAFA',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        '&:hover': { borderColor: validationErrors.symbolImage ? '#FF4D49' : '#FFBD65' }
                                    }}
                                >
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <img src={imagePreview} alt="Preview"
                                                style={{ maxHeight: 100, maxWidth: '100%', borderRadius: '4px' }}
                                            />
                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                size="small"
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    right: -10,
                                                    bgcolor: '#FF4D49',
                                                    color: '#fff',

                                                }}
                                            >
                                                <X size={14} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                            <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#FFF8ED', color: '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={20} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.85rem' }} color="textSecondary">Click to upload flag/symbol</Typography>
                                                <Typography sx={{ fontSize: '0.75rem' }} color="textSecondary">PNG, JPG (max. 2MB)</Typography>
                                            </Stack>
                                        </label>
                                    )}
                                </Box>
                                {validationErrors.symbolImage && (
                                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {validationErrors.symbolImage}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Country Code"
                    description="Are you sure you want to delete this country code? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default FlagsData;